import {
	afterNextRender,
	Component,
	computed,
	DestroyRef,
	effect,
	ElementRef,
	inject,
	input,
	output,
	signal,
	ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { ThemeConfig } from '../../models/theme.model';
import { ThemeMode, ThemeService } from '../../services/theme.service';
import { CardStyle } from '@framework/analytics/models/card-model';
import { CardError } from '../../models/card-model';
import { CardService } from '../../services/card.service';
import {
	cardErrorSeverityCssClass,
	cardErrorSeverityIcon,
	resolvePriorityCardError,
} from '../../utils/card-error-display.util';
import {
	resolveCardIconField,
	resolveCardTextField,
} from '../../utils/card-chrome.util';

/**
 * Check if debug mode is enabled via URL query parameter.
 * This is a fallback for standalone card usage (when not in a layout container).
 * Layout containers should read the URL once and pass debugMode to all cards.
 */
function isDebugMode(): boolean {
	if (typeof window === 'undefined') return false;
	const urlParams = new URLSearchParams(window.location.search);
	return urlParams.get('debug') === 'true';
}

/**
 * Base component for all card types.
 *
 * Provides common inputs, computed signals, and effects that all cards share.
 * Cards can operate in two modes:
 *
 * 1. **Standalone Mode**: Provide data directly via inputs
 *    ```html
 *    <jepto-chart-card [config]="myConfig" [data]="myData" />
 *    ```
 *
 * 2. **Connected Mode**: Provide cardId to fetch from services
 *    ```html
 *    <jepto-chart-card [cardId]="'card_123'" />
 *    ```
 */
@Component({
	selector: 'jepto-base-card',
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: '',
})
export abstract class JeptoBaseCardComponent {
	// ═══════════════════════════════════════════════════════════════════════════
	// SIGNAL INPUTS
	// ═══════════════════════════════════════════════════════════════════════════

	/** Unique identifier for connected mode (fetches config/data from services) */
	cardId = input<string>();

	/** Title displayed at the top of the card */
	title = input<string>();

	/** Subtitle displayed below the title */
	subtitle = input<string>();

	/** Icon to display next to title */
	icon = input<string>();

	/** Theme mode (light/dark/auto) */
	themeMode = input<ThemeMode>('auto');

	/** Color palette identifier */
	palette = input<string>('pal_jepto');

	/** Theme configuration object */
	themeConfig = input<ThemeConfig>();

	/**
	 * When false, skips global ThemeService theme/palette application.
	 * Use in editor modals so nested cards do not repaint report :root.
	 */
	readonly applyThemeGlobally = input(true);

	/** Card-specific style overrides */
	style = input<CardStyle>();

	/** Error state for the card */
	error = input<CardError>();

	/**
	 * Debug mode - shows cardId and debug info.
	 * When used in a layout container, this is set by the container.
	 * For standalone cards, falls back to URL query parameter ?debug=true.
	 */
	debugMode = input<boolean>();

	/** When true, clicking the card selects it and shows the edit overlay. */
	isEditMode = input<boolean>(false);

	// ═══════════════════════════════════════════════════════════════════════════
	// EDIT MODE OUTPUTS
	// ═══════════════════════════════════════════════════════════════════════════

	readonly editCard = output<void>();
	readonly duplicateCard = output<void>();
	readonly saveAsTemplate = output<void>();
	readonly deleteCard = output<void>();

	// ═══════════════════════════════════════════════════════════════════════════
	// SERVICES
	// ═══════════════════════════════════════════════════════════════════════════

	protected themeService = inject(ThemeService);
	protected cardService = inject(CardService, { optional: true });
	protected elementRef = inject(ElementRef);

	// ═══════════════════════════════════════════════════════════════════════════
	// EDIT MODE STATE
	// ═══════════════════════════════════════════════════════════════════════════

	protected isSelected = signal<boolean>(false);

	/** Local error override (e.g. cleared on retry). Subclasses may set via this signal. */
	protected readonly localCardError = signal<CardError | null>(null);

	protected onCardClick(): void {
		if (this.isEditMode()) {
			this.isSelected.set(true);
		}
	}

	// ═══════════════════════════════════════════════════════════════════════════
	// COMPUTED SIGNALS
	// ═══════════════════════════════════════════════════════════════════════════

	/** Base config from CardService when in connected mode */
	protected baseConfig = computed(() => {
		const id = this.cardId();
		if (id && this.cardService) {
			return this.cardService.cardConfig(id)();
		}
		return null;
	});

	/**
	 * Resolved light/dark for card chrome (style.colors.*, image refs, etc.).
	 * Explicit `themeMode` input (`light` | `dark`) wins — e.g. editor preview toggle.
	 * `auto` (default) follows {@link ThemeService} active mode (layout / report).
	 */
	protected activeThemeMode = computed((): 'light' | 'dark' => {
		const mode = this.themeMode();
		if (mode === 'light' || mode === 'dark') {
			return mode;
		}
		return this.themeService.getActiveThemeModeSignal()();
	});

	/** Card config used for title/subtitle/icon; override when not `baseConfig`. */
	protected cardConfigForChrome = computed(() => this.baseConfig());

	/** Effective title - direct input takes priority over service */
	protected effectiveTitle = computed(() =>
		resolveCardTextField(this.title(), this.cardConfigForChrome()?.title)
	);

	/** Effective subtitle - direct input takes priority over service */
	protected effectiveSubtitle = computed(() =>
		resolveCardTextField(
			this.subtitle(),
			this.cardConfigForChrome()?.subtitle
		)
	);

	/** Effective icon - direct input takes priority over service */
	protected effectiveIcon = computed(() =>
		resolveCardIconField(this.icon(), this.cardConfigForChrome()?.icon)
	);

	/** Effective style - direct input takes priority over service */
	protected effectiveStyle = computed(
		() => this.style() ?? this.baseConfig()?.style
	);

	/**
	 * Debug mode - input takes priority (from layout container),
	 * falls back to URL param ?debug=true for standalone cards.
	 */
	protected isDebugMode = computed(() => this.debugMode() ?? isDebugMode());

	/** Config-sourced error — override when card uses `effectiveConfig`. */
	protected configCardErrorSource = computed(
		(): CardError | null | undefined => this.baseConfig()?.error
	);

	/** Data payload error — override when card resolves `effectiveData`. */
	protected dataCardErrorSource = computed(
		(): CardError | null | undefined => undefined
	);

	/** Combined error from input, local state, config, and data (priority order). */
	protected readonly cardError = computed(() =>
		resolvePriorityCardError(
			this.error(),
			this.localCardError(),
			this.configCardErrorSource(),
			this.dataCardErrorSource()
		)
	);

	/** Has error state */
	protected hasError = computed(() => this.cardError() !== null);

	/** Error severity CSS class */
	protected errorSeverityClass = computed(() => {
		const err = this.cardError();
		if (!err) return '';
		return cardErrorSeverityCssClass(err.severity);
	});

	/** Error icon based on severity */
	protected errorIcon = computed(() => {
		const err = this.cardError();
		if (!err) return 'error';
		return cardErrorSeverityIcon(err.severity);
	});

	// ═══════════════════════════════════════════════════════════════════════════
	// EFFECTS
	// ═══════════════════════════════════════════════════════════════════════════

	constructor() {
		const destroyRef = inject(DestroyRef);
		afterNextRender(() => {
			fromEvent<MouseEvent>(document, 'click')
				.pipe(takeUntilDestroyed(destroyRef))
				.subscribe(event => {
					if (!this.isEditMode()) return;
					if (
						!this.elementRef.nativeElement.contains(
							event.target as Node
						)
					) {
						this.isSelected.set(false);
					}
				});
		});

		// Reset selection when edit mode is turned off
		effect(() => {
			if (!this.isEditMode()) {
				this.isSelected.set(false);
			}
		});

		// Apply palette when it changes
		effect(() => {
			const pal = this.palette();
			if (pal && this.themeService && this.applyThemeGlobally()) {
				this.themeService.applyPalette(pal);
			}
		});

		// Load theme config when it changes
		effect(() => {
			const cfg = this.themeConfig();
			if (!cfg || !this.themeService || !this.applyThemeGlobally()) {
				return;
			}
			this.themeService.loadThemeFromConfig(cfg);
		});

		// Apply style overrides when theme or style changes
		effect(() => {
			const theme = this.activeThemeMode();
			const style = this.effectiveStyle();
			this.applyCardStyleOverrides(style, theme);
		});
	}

	/** CSS custom properties we set for card style overrides (so we can clear them on reset). */
	private static readonly CARD_STYLE_PROPERTIES = [
		'--card-padding',
		'--card-content-alignment',
		'--card-background-color',
		'--primary-background-color',
		'--background-primary-color',
		'--background-color',
		'--border-primary-color',
		'--card-border-width',
		'--card-shadow-color',
	] as const;

	/**
	 * Apply card-specific style overrides to CSS custom properties.
	 * When style is undefined, clears any previous overrides so the card falls back to theme/inherited values.
	 */
	private applyCardStyleOverrides(
		style: CardStyle | undefined,
		themeMode: string
	): void {
		const element = this.elementRef?.nativeElement;
		if (!element) return;

		if (!style) {
			JeptoBaseCardComponent.CARD_STYLE_PROPERTIES.forEach(prop =>
				element.style.removeProperty(prop)
			);
			return;
		}

		// Apply card-specific overrides
		if (style.card?.padding) {
			element.style.setProperty('--card-padding', style.card.padding);
		} else {
			element.style.removeProperty('--card-padding');
		}

		if (style.card?.contentAlignment) {
			element.style.setProperty(
				'--card-content-alignment',
				style.card.contentAlignment
			);
		} else {
			element.style.removeProperty('--card-content-alignment');
		}

		// Apply color overrides based on current theme mode
		const colorOverrides =
			style.colors?.[themeMode as 'light' | 'dark']?.card;
		if (colorOverrides) {
			if (colorOverrides.background) {
				const background = colorOverrides.background;
				element.style.setProperty(
					'--card-background-color',
					background
				);
				element.style.setProperty(
					'--primary-background-color',
					background
				);
				element.style.setProperty(
					'--background-primary-color',
					background
				);
				element.style.setProperty('--background-color', background);
			} else {
				element.style.removeProperty('--card-background-color');
				element.style.removeProperty('--primary-background-color');
				element.style.removeProperty('--background-primary-color');
				element.style.removeProperty('--background-color');
			}
			if (colorOverrides.border) {
				element.style.setProperty(
					'--border-primary-color',
					colorOverrides.border
				);
				element.style.setProperty('--card-border-width', '1px');
			} else {
				element.style.removeProperty('--border-primary-color');
				element.style.removeProperty('--card-border-width');
			}
			if (colorOverrides.shadow) {
				element.style.setProperty(
					'--card-shadow-color',
					colorOverrides.shadow
				);
			} else {
				element.style.removeProperty('--card-shadow-color');
			}
		} else {
			// No overrides for this theme: clear so theme/inherited values show
			element.style.removeProperty('--card-background-color');
			element.style.removeProperty('--primary-background-color');
			element.style.removeProperty('--background-primary-color');
			element.style.removeProperty('--background-color');
			element.style.removeProperty('--border-primary-color');
			element.style.removeProperty('--card-border-width');
			element.style.removeProperty('--card-shadow-color');
		}
	}
}
